// The short label of a hierarchical value, for a table chip or a single-line
// cell (#12052). The leaf is the fact; the ancestors are context the column
// header implies, so the label is the leaf alone — unless another path ends
// in the same leaf, in which case it carries the shortest trailing run of
// segments that tells the two apart, up to the full path.
//
// The colliding set is the attribute's own option list plus the other values
// in the same cell. That is what keeps LNW's Asset Factors honest without an
// org-level declaration of which segments name a sub-field (#11552, the
// keeper's clause on rule 33 in #11426): "Market Cap > All" beside
// "Duration > All" shows both parents, while "Private Equity > Venture" in a
// vocabulary with one Venture shows "Venture". Surfaces that want the whole
// path (the overview rail, filter chips, export) do not come here.

export const PATH_SEPARATOR = " > ";

export interface HierarchicalLabelContext {
  // The attribute's option list (`scalar_values`); entries that are not
  // paths are ignored. Null when the value is not bound to a vocabulary.
  options: readonly unknown[] | null | undefined;
  // The other values rendered beside this one, as paths.
  siblings?: readonly (readonly unknown[])[];
}

const segments = (path: readonly unknown[]): string[] => path.map(String);

const tail = (path: readonly string[], count: number): string =>
  path.slice(Math.max(0, path.length - count)).join(PATH_SEPARATOR);

export function hierarchicalValueLabel(
  path: readonly unknown[],
  context: HierarchicalLabelContext
): string {
  const own = segments(path);
  if (own.length === 0) return "";
  const full = own.join(PATH_SEPARATOR);
  const others: string[][] = [];
  const consider = (candidate: unknown): void => {
    if (!Array.isArray(candidate)) return;
    const other = segments(candidate);
    // The value's own path, in the options or listed twice in a cell, is
    // never a collision with itself.
    if (other.join(PATH_SEPARATOR) === full) return;
    others.push(other);
  };
  (context.options ?? []).forEach(consider);
  (context.siblings ?? []).forEach(consider);

  for (let count = 1; count < own.length; count += 1) {
    const candidate = tail(own, count);
    const collides = others.some(
      (other) => other.length >= count && tail(other, count) === candidate
    );
    if (!collides) return candidate;
  }
  return full;
}
